/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import { publishProduct } from './api';
import type { TrajksFormValues } from '../components/Trajks';
import { useKNMIComponentsTranslation } from '../utils/i18n';

type MutationReturnType<T> = UseMutationResult<Headers | null, Error, T>;

interface PublishProductProps {
  data: TrajksFormValues;
  clearForm: () => void;
}

export const usePublishProduct =
  (): MutationReturnType<PublishProductProps> => {
    const { auth } = useAuthenticationContext();
    const { t } = useKNMIComponentsTranslation();
    const dispatch = useDispatch();

    return useMutation({
      mutationFn: async ({ data }) => {
        if (!auth) {
          throw new Error('Authentication context not found');
        }
        return publishProduct(auth, data);
      },
      onSuccess: async (_data, { clearForm }) => {
        dispatch(
          snackbarActions.openSnackbar({
            type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
            key: t('message-publish-success'),
          }),
        );
        clearForm();
      },
    });
  };
